import type { Document } from "@contentful/rich-text-types";
import { Button } from "@ui/components/forms/button/Button";
import { Field } from "@ui/components/forms/field/Field";
import { FormControl } from "@ui/components/forms/form-control/FormControl";
import { FormErrorMessage } from "@ui/components/forms/form-control/FormError";
import { FormLabel } from "@ui/components/forms/form-control/FormLabel";
import { Input } from "@ui/components/forms/input/Input";
import { Flex } from "@ui/components/layout/flex/Flex";
import { ECCOIcon } from "@ui/components/media-and-icons/ecco-icon/ECCOIcon";
import { Form, useFormikContext } from "formik";
import { FORM_ID } from "@components/layouts/footer/footer-email-subscription/constants";
import type { Header, Values } from "@components/layouts/footer/footer-email-subscription/types";
import { PolicyAgreement } from "../policy-agreement";
import { styles } from "./Form.style";
import { useEffect } from "react";

type Props = {
    headers: Header;
    isLoading: boolean;
    policyAgreement: Document;
    email?: string;
};

export const InnerForm = ({ headers, isLoading, policyAgreement, email }: Props) => {
    const { errors, touched, setFieldValue } = useFormikContext<Values>();

    const { requiredLabel, subscribeButton, emailError, emailInputPlaceholder } = headers;

    const hasError = Boolean(errors.email) && Boolean(touched.email);

    useEffect(() => {
        setFieldValue("email", email ?? "");
    }, [email]);

    return (
        <Form id={FORM_ID} noValidate={true} style={styles.form}>
            <Flex {...styles.emailFormContainer}>
                <FormControl
                    data-privacy="true"
                    isRequired
                    isDisabled={Boolean(email)}
                    isInvalid={(!!errors.email && touched.email) as boolean}
                >
                    <FormErrorMessage {...styles.formErrorMessage}>{emailError}</FormErrorMessage>
                    <Field
                        as={Input}
                        id="email"
                        name="email"
                        type="email"
                        placeholder={emailInputPlaceholder}
                        {...styles.fieldStyle}
                        {...(hasError && styles.fieldErrorStyle)}
                    />
                    <FormLabel color={hasError ? "errorOnBlack" : "white"} htmlFor="email">
                        {requiredLabel}
                    </FormLabel>
                </FormControl>
                <Button
                    aria-label={subscribeButton}
                    type="submit"
                    form={FORM_ID}
                    rightIcon={<ECCOIcon name="arrow-newsletter-signup" boxSize={["20", "24"]} />}
                    disabled={isLoading}
                    isLoading={isLoading}
                    {...styles.signUpButton}
                />
            </Flex>
            <PolicyAgreement
                error={errors.policy}
                touched={touched.policy}
                policyAgreement={policyAgreement}
            />
        </Form>
    );
};
